// skipcq: JS-W1028
import { useEffect, useMemo, useRef } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import useBeerReviewTags from '../../hooks/useBeerReviewTags';
import useBeerReviewsForBeer from '../../hooks/useBeerReviewsForBeer';
import useHospitalityReviewsForBrewery from '../../hooks/useHospitalityReviewsForBrewery';
import usePaginatedBeerReviewsForBeer from '../../hooks/usePaginatedBeerReviewsForBeer';
import usePaginatedHospitalityReviewsForBrewery from '../../hooks/usePaginatedHospitalityReviewsForBrewery';
import { useReviewDetailsSeen } from '../../context/ReviewDetailsSeenContext';
import BeerProfileSummaryStatsCard from '../pintPortfolio/BeerProfileSummaryStatsCard';
import TapRatingBar from '../ratings/TapRatingBar';
import BeerReviewCard from '../reviews/BeerReviewCard';
import HospitalityReviewCard from '../reviews/HospitalityReviewCard';
import ReviewSeenDwellObserver from './ReviewSeenDwellObserver';
import { REVIEW_DETAILS_CONTENT_MAX_WIDTH, REVIEW_DETAILS_PAGE_SIZE } from '../../utils/reviewDetails/reviewDetailsConstants';
import { trackReviewDetailsEmptyState } from '../../utils/brewhousePortalAnalytics';
import { TAB_ALL_REVIEWS } from './ReviewDetailsTabs';

function ReviewDetailsReviewList({
  reviews,
  reviewType,
  breweryId,
  beerId,
  activeTab,
  itemType,
  itemId,
  loading,
  loadingMore,
  error,
  hasMore,
  onLoadMore,
  showReactionDock,
  tagLabelMap,
}) {
  const { isUnseen } = useReviewDetailsSeen();
  const sentinelRef = useRef(null);
  const trackedStateRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof onLoadMore !== 'function') {
      return undefined;
    }

    const observer = new globalThis.IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore]);

  useEffect(() => {
    if (loading || !breweryId || !itemType || !itemId) {
      return;
    }

    const stateKey = `${breweryId}:${activeTab}:${itemType}:${itemId}`;
    if (error) {
      const errorKey = `${stateKey}:error`;
      if (trackedStateRef.current === errorKey) {
        return;
      }
      trackedStateRef.current = errorKey;
      trackReviewDetailsEmptyState({
        breweryId,
        tab: activeTab,
        reason: 'item-reviews-error',
        itemType,
        itemId,
      });
      return;
    }

    if (reviews.length === 0) {
      const emptyKey = `${stateKey}:empty`;
      if (trackedStateRef.current === emptyKey) {
        return;
      }
      trackedStateRef.current = emptyKey;
      trackReviewDetailsEmptyState({
        breweryId,
        tab: activeTab,
        reason: 'no-item-reviews',
        itemType,
        itemId,
      });
    }
  }, [
    activeTab,
    breweryId,
    error,
    itemId,
    itemType,
    loading,
    reviews.length,
  ]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Unable to load reviews. Please try again later.
      </Alert>
    );
  }

  if (reviews.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No reviews to display.
      </Typography>
    );
  }

  return (
    <Box>
      {reviews.map((review) => {
        const unseen = isUnseen(reviewType, review.id);
        const card = reviewType === 'beer' ? (
          <BeerReviewCard
            review={review}
            variant="profile"
            tagLabelMap={tagLabelMap}
            breweryId={breweryId}
            beerId={beerId}
            showBreweryReactionDock={showReactionDock}
            showNewChip={activeTab !== TAB_ALL_REVIEWS && unseen}
            analyticsSource="review-details"
            itemType="beer"
          />
        ) : (
          <HospitalityReviewCard
            review={review}
            variant="profile"
            breweryId={breweryId}
            showBreweryReactionDock={showReactionDock}
            showNewChip={activeTab !== TAB_ALL_REVIEWS && unseen}
            analyticsSource="review-details"
            itemType="brewery"
          />
        );

        return (
          <ReviewSeenDwellObserver
            key={review.id}
            reviewType={reviewType}
            reviewId={review.id}
            isUnseen={unseen}
          >
            {card}
          </ReviewSeenDwellObserver>
        );
      })}
      {loadingMore ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : null}
      {hasMore ? <Box ref={sentinelRef} sx={{ height: 1 }} aria-hidden /> : null}
      {!hasMore && reviews.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Showing all reviews
        </Typography>
      ) : null}
    </Box>
  );
}

ReviewDetailsReviewList.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.object).isRequired,
  reviewType: PropTypes.oneOf(['beer', 'hospitality']).isRequired,
  breweryId: PropTypes.string,
  beerId: PropTypes.string,
  activeTab: PropTypes.oneOf(['new-reviews', 'all-reviews']).isRequired,
  itemType: PropTypes.oneOf(['beer', 'brewery']),
  itemId: PropTypes.string,
  loading: PropTypes.bool,
  loadingMore: PropTypes.bool,
  error: PropTypes.string,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  showReactionDock: PropTypes.bool,
  tagLabelMap: PropTypes.objectOf(PropTypes.string),
};

function ReviewDetailsDetailPanel({
  selectedItem,
  activeTab,
  breweryId,
  beerCatalogById,
}) {
  const { tagLabelMap } = useBeerReviewTags();
  const unseenOnly = activeTab !== TAB_ALL_REVIEWS;
  const enabled = Boolean(selectedItem && breweryId);

  const beerId = selectedItem?.itemType === 'beer' ? selectedItem.itemId : '';
  const isBeerItem = selectedItem?.itemType === 'beer';
  const isBreweryItem = selectedItem?.itemType === 'brewery';

  const beerCatalogItem = isBeerItem ? beerCatalogById.get(beerId) : null;

  const {
    reviews: beerReviews,
    loading: beerLoading,
    error: beerError,
  } = useBeerReviewsForBeer({
    beerId,
    enabled: enabled && isBeerItem && unseenOnly,
    reviewLimit: REVIEW_DETAILS_PAGE_SIZE,
  });

  const {
    reviews: paginatedBeerReviews,
    loading: paginatedBeerLoading,
    loadingMore: paginatedBeerLoadingMore,
    error: paginatedBeerError,
    hasMore: paginatedBeerHasMore,
    loadMore: loadMoreBeerReviews,
  } = usePaginatedBeerReviewsForBeer({
    beerId,
    enabled: enabled && isBeerItem && !unseenOnly,
  });

  const {
    reviews: hospitalityReviews,
    loading: hospitalityLoading,
    error: hospitalityError,
  } = useHospitalityReviewsForBrewery({
    breweryId,
    enabled: enabled && isBreweryItem && unseenOnly,
    limit: REVIEW_DETAILS_PAGE_SIZE,
  });

  const {
    reviews: paginatedHospitalityReviews,
    loading: paginatedHospitalityLoading,
    loadingMore: paginatedHospitalityLoadingMore,
    error: paginatedHospitalityError,
    hasMore: paginatedHospitalityHasMore,
    loadMore: loadMoreHospitalityReviews,
  } = usePaginatedHospitalityReviewsForBrewery({
    breweryId,
    enabled: enabled && isBreweryItem && !unseenOnly,
  });

  const displayBeerReviews = useMemo(() => (
    unseenOnly ? beerReviews : paginatedBeerReviews
  ), [beerReviews, paginatedBeerReviews, unseenOnly]);

  const displayHospitalityReviews = useMemo(() => (
    unseenOnly ? hospitalityReviews : paginatedHospitalityReviews
  ), [hospitalityReviews, paginatedHospitalityReviews, unseenOnly]);

  if (!selectedItem) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Select an item to view reviews.
        </Typography>
      </Box>
    );
  }

  const headerStats = isBeerItem ? (
    <Box sx={{ mt: 2 }}>
      <BeerProfileSummaryStatsCard
        totalReviews={beerCatalogItem?.totalReviews ?? selectedItem.totalReviews}
        checkinsCount={beerCatalogItem?.checkinsCount ?? selectedItem.checkinsCount}
      />
    </Box>
  ) : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          maxWidth: REVIEW_DETAILS_CONTENT_MAX_WIDTH,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="h6" component="h2">
            {selectedItem.name}
          </Typography>
          <TapRatingBar rating={selectedItem.averageRating} size="compact" showNumeric />
        </Box>
        {headerStats}
        <Box sx={{ mt: 2 }}>
          {isBeerItem ? (
            <ReviewDetailsReviewList
              reviews={displayBeerReviews}
              reviewType="beer"
              breweryId={breweryId}
              beerId={beerId}
              activeTab={activeTab}
              itemType="beer"
              itemId={beerId}
              loading={unseenOnly ? beerLoading : paginatedBeerLoading}
              loadingMore={paginatedBeerLoadingMore}
              error={unseenOnly ? beerError : paginatedBeerError}
              hasMore={!unseenOnly && paginatedBeerHasMore}
              onLoadMore={loadMoreBeerReviews}
              showReactionDock
              tagLabelMap={tagLabelMap}
            />
          ) : (
            <ReviewDetailsReviewList
              reviews={displayHospitalityReviews}
              reviewType="hospitality"
              breweryId={breweryId}
              activeTab={activeTab}
              itemType="brewery"
              itemId={breweryId}
              loading={unseenOnly ? hospitalityLoading : paginatedHospitalityLoading}
              loadingMore={paginatedHospitalityLoadingMore}
              error={unseenOnly ? hospitalityError : paginatedHospitalityError}
              hasMore={!unseenOnly && paginatedHospitalityHasMore}
              onLoadMore={loadMoreHospitalityReviews}
              showReactionDock
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

ReviewDetailsDetailPanel.propTypes = {
  selectedItem: PropTypes.shape({
    itemType: PropTypes.oneOf(['beer', 'brewery']).isRequired,
    itemId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    averageRating: PropTypes.number,
    totalReviews: PropTypes.number,
    checkinsCount: PropTypes.number,
  }),
  activeTab: PropTypes.oneOf(['new-reviews', 'all-reviews']).isRequired,
  breweryId: PropTypes.string,
  beerCatalogById: PropTypes.instanceOf(Map).isRequired,
};

export default ReviewDetailsDetailPanel;
